import { IconButton } from '@moonpig/launchpad-components'
import React from 'react'
import { useAnalyticsHandlers } from '@moonpig/launchpad-analytics'
import { IconSystemArrowForward } from '@moonpig/launchpad-assets'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { milestoneResultsEvent } from '../../../pages/SearchPage/events'
import { useSearchAnalytics } from '../../../pages/SearchPage/analytics'
import {
  IntegerFieldTypes,
  IntegerFilterInput,
  useIntegerInputFilter,
} from '../../IntegerFilterInput'
import { useSearchStore } from '../../../store/SearchStore'
import { FilterSource } from '../../../types'

const StyledInputForm = styled.form`
  display: flex;
  width: 100%;

  ${s({
    gap: 8,
    alignItems: { xs: 'end', md: 'start', lg: 'end' },
    flexDirection: { xs: 'row', md: 'column', lg: 'row' },
  })}
`

const StyledIconButton = styled(IconButton)`
  width: 44px;
  height: 44px;
  margin: 2px;

  ${s({
    bgcolor: 'colorInteractionButton',
    color: 'colorTextInverted',
  })}

  &:not(:disabled) {
    &:hover,
    &:hover:active {
      ${s({
        bgcolor: 'colorInteractionButtonHover',
        color: 'colorTextInverted',
      })}
    }

    &:focus,
    &:active {
      ${s({
        bgcolor: 'colorInteractionButtonFocus',
        color: 'colorTextInverted',
      })}
    }
  }
`

export const InputForm = ({
  filter,
  ctaTitle,
}: {
  ctaTitle: string
  filter: {
    integerId: string
    id: string
  }
}) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const onIntegerFilterChange = useSearchStore(
    store => store.onIntegerFilterChange,
  )
  const applyIntegerFilter = useSearchStore(store => store.applyIntegerFilter)
  const integerFilters = useSearchStore(store => store.integerFilters)

  const integerFilter = integerFilters.find(
    f => f.integerId === filter.integerId,
  )
  const integerFilterValue = integerFilter?.value || null

  const { createConfiguration, isIntegerFilterValueValid } =
    useIntegerInputFilter(integerFilter)

  const hasValidValue = isIntegerFilterValueValid(integerFilterValue)

  const pageType = useSearchStore(store => store.pageContext.type)

  const milestoneLabel = integerFilterValue?.toString() || ''

  // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
  const milestoneSubmitEvent = milestoneResultsEvent()
    .withEventData({
      category: `${pageType} action`.toLowerCase(),
      label: milestoneLabel,
    })
    .withContentData({
      content_type:
        `${pageType} action | show milestone results | ${milestoneLabel}`
          .toLowerCase()
          .substring(0, 100),
    })

  const submitHandlers = useAnalyticsHandlers<HTMLFormElement>(
    isLaunchpadAnalytics && hasValidValue
      ? { onSubmit: milestoneSubmitEvent }
      : {},
    {
      onSubmit: e => {
        e.preventDefault()
        applyIntegerFilter({
          filterId: filter.id,
          value: integerFilterValue,
          source: FilterSource.EntryPointBanner,
        })
      },
    },
  )

  return (
    <StyledInputForm {...submitHandlers}>
      <IntegerFilterInput
        onChange={(ageInput: string) =>
          onIntegerFilterChange({
            integerFilterId: filter.integerId,
            value: Number(ageInput) || /* istanbul ignore next */ null,
          })
        }
        input={createConfiguration(IntegerFieldTypes.age, integerFilterValue)}
        hasValidValue={hasValidValue}
        noBorder
        inputCtaComponent={
          <StyledIconButton
            type="submit"
            icon={IconSystemArrowForward}
            label={ctaTitle}
            disabled={!hasValidValue || !integerFilterValue}
          />
        }
      />
    </StyledInputForm>
  )
}
